<template>
  <!-- As iniciais ficam SEMPRE desenhadas, e a foto cobre por cima.

       Não é redundância: foto que não pinta — demorou, o endereço expirou, a rede caiu — deixava
       um círculo vazio, e vazio aqui era a cor padrão do Vuetify, quase preta no tema claro.
       Assim o pior caso é a bolinha com as iniciais, que é o caso normal de quem não tem foto.

       A cor de fundo vai sempre, pelo mesmo motivo: nunca cair no padrão do componente. -->
  <v-avatar :size="size" :class="avatar.classes">
    <span class="user-avatar-initials" :style="{fontSize: initialsSize}">{{ initials }}</span>
    <v-img v-if="photo" :src="photo" alt="" cover class="user-avatar-photo"/>
  </v-avatar>
</template>

<script lang="ts">
import {Component, Vue, Prop, toNative} from 'vue-facing-decorator'
import {personAvatar} from '@/utils/personAvatar'

/**
 * A cara de uma pessoa: a foto dela, ou as iniciais sobre uma cor.
 *
 * Nasceu dentro do `App.vue`, como um `v-avatar` escrito à mão com cor fixa e um método
 * `initials` ao lado. Saiu de lá porque passou a ter quatro consumidores — a barra, o perfil, as
 * pessoas de um ambiente e os convites —, e porque desenhar gente é a mesma coisa nos quatro.
 *
 * **A cor vem do id da conta** (`user`), pelo `personColor`: é derivada, nunca gravada, então a
 * mesma pessoa tem a mesma cor em toda tela e em toda sessão. Era `teal-accent-4` fixo, e com uma
 * lista de gente isso vira uma parede de bolinhas iguais. Já veio do e-mail, que deixou de ir às
 * telas de uso.
 *
 * **As iniciais saem do `label`** — o que se lê da pessoa, que quem desenha decide: o nome, ou o
 * e-mail de quem não tem nome numa tela de administração.
 *
 * As iniciais e as cores são do `personAvatar`, que a referência do markdown também usa.
 */
@Component({name: 'ontos-user-avatar'})
class UserAvatar extends Vue {
  @Prop({type: Number, required: true}) readonly user!: number
  @Prop({type: String, default: ''}) readonly label!: string
  @Prop({type: String, default: ''}) readonly photo!: string
  @Prop({type: Number, default: 32}) readonly size!: number

  get avatar() {
    return personAvatar(this.user, this.label)
  }

  get initials(): string {
    return this.avatar.initials
  }

  /**
   * A inicial medida em cima do círculo, e não em `em`.
   *
   * `em` segue a fonte HERDADA, que é a do texto em volta — o mesmo componente desenharia a
   * mesma letrinha nos 32px da barra e nos 96px do perfil. Dois quintos é a proporção que o
   * Vuetify usa nos avatares dele.
   */
  get initialsSize(): string {
    return `${Math.round(this.size * 0.4)}px`
  }

}

export default toNative(UserAvatar)
</script>

<style scoped>
/* Acompanha o tamanho do avatar em vez de ser fixo: o mesmo componente desenha 32px na barra e
   96px no perfil, e inicial de tamanho fixo ficaria perdida no meio do círculo grande. */
.user-avatar-initials {
  font-weight: 500;
  letter-spacing: 0.02em;
  line-height: 1;
}

/* Por cima das iniciais, cobrindo o círculo inteiro. `inset: 0` e não `height: 100%` porque o
   avatar é um flex: sem tirá-la do fluxo, a imagem dividiria o espaço com a letra em vez de
   escondê-la. */
.user-avatar-photo {
  inset: 0;
  position: absolute;
}
</style>
